"use client"

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { type RefObject } from "react"

import { parseProfile } from "@/features/auth/api/profile-parsers"
import {
  STREAMER_ROLE,
  type Profile,
  type StreamerNote,
  type StreamerTag,
} from "@/features/auth/api/types"
import {
  fetchAccountsPage,
  type GetUsersSortDirection,
} from "@/features/admin/shared/api/paged-accounts"
import type { StreamerWritePayload } from "@/features/admin/shared/api/types"
import {
  useInfiniteListAdapter,
  type InfiniteListScrollOptions,
} from "@/react-query/adapters/use-infinite-query-adapter"
import { apiClient } from "@/react-query/api-client"
import { API_ROUTES } from "@/react-query/api-routes"
import type { ApiEnvelope, PagedData } from "@/react-query/api-types"
import { CACHE_TIME } from "@/react-query/cache-times"
import { queryKeys } from "@/react-query/query-keys"

const adminUsersQueryKey = ["admin", "users"] as const

function invalidateStreamerAccountLists(
  queryClient: ReturnType<typeof useQueryClient>
) {
  void queryClient.invalidateQueries({ queryKey: adminUsersQueryKey })
}

export type StreamStatsUserList = {
  streamerName: string
  channels: {
    channelId: number
    platform: string
    channelName: string
    channelUrl: string
    channelImageUrl: string
    platformSpecificId: string
    followerCount: number
    language: string[]
  }[]
}

type StreamStatsUserListPageData = {
  content: StreamStatsUserList[]
  totalElements: number
  page: number
  size: number
}

type StreamStatsUserListResponse = {
  data: StreamStatsUserListPageData & { hasMore: boolean }
}

const parseStreamStatsUserList = (
  data: StreamStatsUserList[]
): StreamStatsUserList[] =>
  data.map((item) => ({
    streamerName: item.streamerName,
    channels: item.channels,
  }))

type QueryOptions = { skip?: boolean }

type InfiniteStreamerParams = {
  query?: string
  tagIds?: number[]
  sortBy?: string
  sortDirection?: GetUsersSortDirection
  managerId?: string
  size?: number
}

export const DEFAULT_STREAMER_LIST_SORT = {
  sortBy: "segment",
  sortDirection: "asc",
} as const

/** `totalElements` from the streamer list query. Same cache key, so no second request. */
export function useActiveStreamerAccountsTotal(options?: { skip?: boolean }) {
  const result = useInfiniteStreamerAccountsQuery(
    { size: 20, ...DEFAULT_STREAMER_LIST_SORT },
    { enabled: !options?.skip }
  )
  return result.data?.pages[0]?.totalElements ?? 0
}

export function useInfiniteStreamerAccountsQuery(
  params: InfiniteStreamerParams,
  {
    enabled = true,
    scroll,
  }: { enabled?: boolean; scroll?: InfiniteListScrollOptions } = {}
) {
  return useInfiniteListAdapter({
    queryKey: queryKeys.infiniteAdminUsers({
      types: STREAMER_ROLE,
      query: params.query,
      tagIds: params.tagIds?.length ? params.tagIds : undefined,
      sortBy: params.sortBy,
      sortDirection: params.sortDirection,
      managerId: params.managerId,
      size: params.size,
    }),
    params,
    staleTime: CACHE_TIME.LIVE,
    gcTime: CACHE_TIME.BALANCED,
    fetchPage: (filters, page) =>
      fetchAccountsPage(
        API_ROUTES.adminStreamerAccounts,
        {
          query: filters.query,
          tagIds: filters.tagIds,
          sortBy: filters.sortBy,
          sortDirection: filters.sortDirection,
          managerId: filters.managerId,
          size: filters.size,
        },
        page
      ),
    getNextPage: (lastPage) =>
      lastPage.hasMore ? lastPage.page + 1 : undefined,
    enabled,
    selectItems: (page) => page.content,
    scroll,
  })
}

export function useInfiniteStreamerAccounts({
  query,
  tagIds,
  sortBy,
  sortDirection,
  managerId,
  skip = false,
  scrollContainerRef,
}: {
  query?: string
  tagIds?: number[]
  sortBy?: string
  sortDirection?: GetUsersSortDirection
  managerId?: string
  skip?: boolean
  scrollContainerRef?: RefObject<HTMLElement | null>
}) {
  const result = useInfiniteStreamerAccountsQuery(
    {
      query,
      tagIds,
      sortBy,
      sortDirection,
      managerId,
      size: 20,
    },
    {
      enabled: !skip,
      scroll: scrollContainerRef
        ? { containerRef: scrollContainerRef }
        : undefined,
    }
  )

  return {
    users: result.items,
    totalElements: result.data?.pages[0]?.totalElements ?? 0,
    isLoading: !skip && result.isLoading,
    isFetching: result.isFetching,
    hasMore: result.hasMore,
    loadMoreRef: result.loadMoreRef,
    loadMore: result.loadMore,
    reset: () => result.refetch(),
  }
}

export function useGetStreamerTagsQuery(
  params: { search?: string; size?: number } = {},
  options?: QueryOptions & {
    scrollContainerRef?: RefObject<HTMLElement | null>
  }
) {
  const skip = options?.skip ?? false
  const queryParams = {
    search: params.search?.trim() || undefined,
    size: params.size ?? 20,
  }
  const result = useInfiniteListAdapter({
    queryKey: queryKeys.infiniteAdminTags(queryParams),
    params: queryParams,
    enabled: !skip,
    staleTime: Number.POSITIVE_INFINITY,
    refetchOnMount: true,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    fetchPage: async (filters, page) => {
      const response = await apiClient<ApiEnvelope<PagedData<StreamerTag>>>(
        API_ROUTES.adminTags,
        {
          params: {
            ...(filters.search?.trim() && { search: filters.search.trim() }),
            page,
            size: filters.size,
          },
        }
      )
      const { content, totalElements } = response.data
      const resolvedPage = response.data.page ?? page
      const resolvedSize = response.data.size ?? filters.size
      return {
        content,
        totalElements,
        page: resolvedPage,
        size: resolvedSize,
        hasMore: (resolvedPage + 1) * resolvedSize < totalElements,
      }
    },
    getNextPage: (lastPage) =>
      lastPage.hasMore ? lastPage.page + 1 : undefined,
    selectItems: (page) => page.content,
    scroll: options?.scrollContainerRef
      ? { containerRef: options.scrollContainerRef }
      : undefined,
  })

  return {
    data: { content: result.items },
    tags: result.items,
    isLoading: !skip && result.isLoading,
    isFetching: result.isFetching,
    isFetchingNextPage: result.isFetchingNextPage,
    hasMore: result.hasMore,
    loadMoreRef: result.loadMoreRef,
    loadMore: result.loadMore,
  }
}

export function useGetStreamStatsUserListQuery(
  params: { query?: string; page?: number; size?: number },
  options?: QueryOptions
) {
  return useQuery({
    queryKey: queryKeys.adminStreamers(params),
    queryFn: async (): Promise<StreamStatsUserListResponse> => {
      const response = await apiClient<
        ApiEnvelope<StreamStatsUserListPageData>
      >(API_ROUTES.adminStreamerAnalytics, {
        params: {
          ...(params.query?.trim() && { search: params.query.trim() }),
          ...(params.page !== undefined && { page: params.page }),
          ...(params.size !== undefined && { size: params.size }),
        },
      })
      const { data } = response
      return {
        data: {
          content: parseStreamStatsUserList(data.content),
          totalElements: data.totalElements,
          page: data.page,
          size: data.size,
          hasMore: (data.page + 1) * data.size < data.totalElements,
        },
      }
    },
    enabled: !options?.skip,
    gcTime: 0,
  })
}

export function useInfiniteStreamStatsUserList({
  query,
  skip = false,
  scrollContainerRef,
}: {
  query?: string
  skip?: boolean
  scrollContainerRef?: RefObject<HTMLElement | null>
}) {
  const params = { query, size: 20 }
  const result = useInfiniteListAdapter({
    queryKey: queryKeys.infiniteAdminStreamers(params),
    params,
    enabled: !skip,
    staleTime: CACHE_TIME.LIVE,
    gcTime: CACHE_TIME.BALANCED,
    fetchPage: async (filters, page) => {
      const response = await apiClient<
        ApiEnvelope<StreamStatsUserListPageData>
      >(API_ROUTES.adminStreamerAnalytics, {
        params: {
          ...(filters.query?.trim() && { search: filters.query.trim() }),
          page,
          size: filters.size,
        },
      })
      return {
        data: {
          ...response.data,
          content: parseStreamStatsUserList(response.data.content),
          hasMore:
            (response.data.page + 1) * response.data.size <
            response.data.totalElements,
        },
      }
    },
    getNextPage: (lastPage) =>
      lastPage.data.hasMore ? lastPage.data.page + 1 : undefined,
    selectItems: (page) => page.data.content,
    scroll: { containerRef: scrollContainerRef },
  })

  return {
    users: result.items,
    isLoading: !skip && result.isLoading,
    isFetching: result.isFetching,
    isError: result.isError,
    hasMore: result.hasMore,
    loadMoreRef: result.loadMoreRef,
    loadMore: result.loadMore,
    reset: () => result.refetch(),
  }
}

export function useGetAccountQuery(id: string, options?: QueryOptions) {
  return useQuery({
    queryKey: queryKeys.adminAccount(id),
    queryFn: async () => {
      const response = await apiClient<ApiEnvelope<Profile>>(
        API_ROUTES.adminStreamerAccount(id)
      )
      return parseProfile(response.data)
    },
    enabled: !options?.skip,
    gcTime: 0,
  })
}

export function useCreateStreamerMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (streamer: StreamerWritePayload & { password?: string }) => {
      const { password, ...rest } = streamer
      return apiClient<Profile>(API_ROUTES.adminStreamerAccounts, {
        method: "POST",
        body: {
          ...rest,
          streamerName: streamer.streamingUsername,
          ...(password ? { password } : {}),
        },
      })
    },
    onSuccess: () => {
      invalidateStreamerAccountLists(queryClient)
      queryClient.invalidateQueries({ queryKey: ["admin", "tags"] })
    },
  })
}

export function useUpdateStreamerMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      id,
      ...streamer
    }: { id: string } & StreamerWritePayload) => {
      return apiClient<Profile>(API_ROUTES.adminStreamerAccount(id), {
        method: "PUT",
        body: streamer,
      })
    },
    onSuccess: (_data, { id }) => {
      invalidateStreamerAccountLists(queryClient)
      queryClient.invalidateQueries({ queryKey: queryKeys.adminAccount(id) })
      queryClient.invalidateQueries({ queryKey: queryKeys.profileDetail() })
      queryClient.invalidateQueries({ queryKey: ["admin", "tags"] })
    },
  })
}

export function useCreateStreamerTagMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (body: { name: string }) => {
      const response = await apiClient<ApiEnvelope<StreamerTag>>(
        API_ROUTES.adminTagsCreate,
        {
          method: "POST",
          body,
        }
      )
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "tags"] })
      invalidateStreamerAccountLists(queryClient)
    },
  })
}

export function useUpdateStreamerTagMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, name }: { id: number; name: string }) => {
      const response = await apiClient<ApiEnvelope<StreamerTag>>(
        API_ROUTES.adminTag(id),
        {
          method: "PUT",
          body: { name },
        }
      )
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "tags"] })
      invalidateStreamerAccountLists(queryClient)
    },
  })
}

export function useDeleteStreamerTagMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (tagId: number) =>
      apiClient<void>(API_ROUTES.adminTag(tagId), { method: "DELETE" }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "tags"] })
      invalidateStreamerAccountLists(queryClient)
    },
  })
}

export function useUpdateStreamerAccountStatusMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      id,
      status,
    }: {
      id: string
      status: "activate" | "deactivate"
    }) =>
      apiClient<void>(API_ROUTES.adminStreamerAccountStatus(id, status), {
        method: "PUT",
      }),
    onSuccess: () => invalidateStreamerAccountLists(queryClient),
  })
}

export function useResendActivationEmailMutation() {
  return useMutation({
    mutationFn: ({ id }: { id: string }) =>
      apiClient<void>(API_ROUTES.adminStreamerResendInvitation(id), {
        method: "POST",
      }),
  })
}

export function useSendPasswordResetEmailMutation() {
  return useMutation({
    mutationFn: ({ id }: { id: string }) =>
      apiClient<void>(API_ROUTES.adminStreamerResetPassword(id), {
        method: "POST",
      }),
  })
}

export function useRetryStreamerStreamStatsSyncMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id }: { id: string }) =>
      apiClient<void>(API_ROUTES.adminStreamerRetryStreamStatsSync(id), {
        method: "POST",
      }),
    onSuccess: () => invalidateStreamerAccountLists(queryClient),
  })
}

export function useStreamStatsBackfillMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: () =>
      apiClient<ApiEnvelope<unknown>>(API_ROUTES.adminStreamStatsBackfill, {
        method: "POST",
        params: { dryRun: false },
      }),
    onSuccess: () => invalidateStreamerAccountLists(queryClient),
  })
}

export function useCreateStreamerNoteMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      note,
      streamerId,
    }: {
      note: StreamerNote
      streamerId: string
    }) =>
      apiClient<unknown>(API_ROUTES.adminStreamerNotes(streamerId), {
        method: "POST",
        body: { content: note.content },
      }),
    onSuccess: (_data, { streamerId }) => {
      invalidateStreamerAccountLists(queryClient)
      queryClient.invalidateQueries({
        queryKey: queryKeys.adminAccount(streamerId),
      })
    },
  })
}
